/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { Link } from "react-router-dom";
import {
  Box,
  FlexContainer,
  Spacer,
  Table,
  Text,
  theme,
} from "@nordcloud/gnui";
import { BrickLoader, UniversalWrap } from "~/components";
import { ROUTES } from "~/routing/routes";
import { COLORS } from "./constants";
import { ChartContainer } from "./styles";
import { TopSpendingAppsChart } from "./TopSpendingAppsChart";
import { TopSpendingAppsWidgetRow } from "./TopSpendingAppsWidgetRow";
import { useTopSpendingApplications } from "./useTopSpendingApplications";

type Props = {
  isCurrentBillingPeriod: boolean;
};

export function TopSpendingAppsWidget({ isCurrentBillingPeriod }: Props) {
  const { data, topSpendingApplicationSum, loading, error } =
    useTopSpendingApplications();

  return (
    <Box minHeight="100%">
      <FlexContainer justifyContent="space-between">
        <Text isTitle tag="header" weight="medium">
          Top spending applications
        </Text>
        <Text
          size="sm"
          tag="div"
          data-testid="top-spending-applications__links"
        >
          <Link to={ROUTES.applications.index}>See all</Link>
        </Text>
      </FlexContainer>
      <Spacer height={theme.spacing.spacing02} />
      <UniversalWrap
        errorProps={{ error }}
        loaderProps={{
          loading,
          Component: BrickLoader,
          viewBox: "0 0 2 1",
        }}
      >
        <FlexContainer
          alignItems="flex-start"
          columnGap={theme.spacing.spacing08}
        >
          <ChartContainer css={{ alignSelf: "center" }}>
            <TopSpendingAppsChart
              innerText={`~${topSpendingApplicationSum}`}
              data={data.appSpendsByNameValue.map((spend, index) => ({
                ...spend,
                color:
                  spend.name === "Unallocated"
                    ? theme.color.support.red
                    : COLORS[index],
              }))}
            />
          </ChartContainer>
          <Table small>
            <Table.thead>
              <Table.tr>
                <Table.th>Name</Table.th>
                <Table.th>This month</Table.th>
                <When condition={isCurrentBillingPeriod}>
                  <Table.th>Forecast</Table.th>
                </When>
              </Table.tr>
            </Table.thead>
            <Table.tbody>
              {data.allApps.map(
                (
                  { name, nid, currentMonth, forecast, previousMonth },
                  index
                ) => (
                  <TopSpendingAppsWidgetRow
                    key={name}
                    nid={nid}
                    name={name}
                    currentMonth={currentMonth}
                    forecast={forecast}
                    previousMonth={previousMonth}
                    index={index}
                    isCurrentBillingPeriod={isCurrentBillingPeriod}
                  />
                )
              )}
            </Table.tbody>
          </Table>
        </FlexContainer>
      </UniversalWrap>
    </Box>
  );
}
